/**
 * Les ERREURS lues des portes du coffre — le moule, les trois portes DOSSIER, et le
 * 410 d'une consultation anonymisée.
 *
 * Module frère de `ged-owned-by-tender`, qui reste le point d'entrée du sujet et
 * ré-exporte ce qui est ici : `ged-folders.ts` n'a pas changé d'import. Coupé le
 * 2026-09-23 : le hub était à 300 lignes PILE, le plafond du dépôt, et le 410 y
 * entrait.
 *
 * ## 🔴 Le 410 `TENDER_ANONYMIZED` sortait en FRANÇAIS (mesuré le 2026-09-23)
 *
 * Le coffre refuse en 410 de modifier une pièce ou un dossier d'une consultation
 * anonymisée, et d'y ranger quoi que ce soit (`api/_ged_piece_anonymisee`,
 * `api/_ged_folders_ao`). L'écran éteint ces gestes quand il SAIT la consultation
 * anonymisée ; le refus n'arrive donc que d'un écran périmé — l'anonymisation
 * faite entre deux rafraîchissements — et c'est là qu'il doit se lire. Or aucun
 * lecteur ne connaissait le code (`grep TENDER_ANONYMIZED frontend/src/lib/api`
 * → 0 ligne) : le toast montrait la phrase du serveur mot pour mot, en français
 * dans l'interface anglaise. L'onglet « Documents » des quatre fiches passe par
 * le même lecteur (`useEntityDocumentsTabState.refusDe`) : il en profite.
 *
 * ⚠️ **La phrase de la CONSULTATION, pas celle de la pièce ni du dossier.** Le
 * même 410 sort pour la pièce elle-même (la renommer) ET pour sa DESTINATION (la
 * ranger, la déposer, la copier dans un dossier anonymisé), et le corps ne dit
 * pas lequel : « Pièce d'une consultation anonymisée… » serait faux sur un dépôt,
 * où la pièce n'existe pas encore. On dit donc ce que dit le serveur — la phrase
 * UNIQUE, `sales.tenders.errors.TENDER_ANONYMIZED`, celle de la fiche AO — dans
 * la langue de l'écran. Vraie dans tous les cas, et une seule rédaction.
 */

import i18next, { type TFunction } from 'i18next'

import { ANONYMISEE_FR } from '@/hooks/useTenders.refus'
import { ApiError, extractStructuredApiError } from '@/lib/api-error'
import { estUnIncident, phraseDeRefusTransverse } from '@/lib/api-error-transverse'

import { phraseDUnDossierQuiPorteUnePieceEpinglee } from './ged-dossier-epingle'
import {
    GED_FOLDER_OWNED_BY_TENDER,
    phraseDeCreationDunDossier,
    phraseDunDossierSysteme,
    phraseDuRefusDUnDossier,
    phraseDuRefusDUnDossierDeplace,
} from './ged-owned-by-tender-folder'

/** 410 — consultation anonymisée : `api/sales/tenders/_helpers.modifiable`. */
export const TENDER_ANONYMIZED = 'TENDER_ANONYMIZED'

/** La phrase d'un refus d'ANONYMISATION, ou `null` si ce n'en est pas un.
 *  Hors application (i18next non initialisé), la phrase française de l'écran —
 *  jamais la clé brute, jamais une chaîne vide qui passerait pour un succès. */
export function phraseDUneConsultationAnonymisee(t: TFunction, erreur: unknown): string | null {
    if (!erreur || typeof erreur !== 'object') return null
    if ((erreur as { code?: unknown }).code !== TENDER_ANONYMIZED) return null
    const phrase: unknown = t('sales.tenders.errors.TENDER_ANONYMIZED', {
        defaultValue: ANONYMISEE_FR,
    })
    return typeof phrase === 'string' && phrase.trim() ? phrase : ANONYMISEE_FR
}

/** Le 409 d'un dossier LIBRE qu'on range dans l'arbre d'une consultation, ou `null`.
 *  C'est la DESTINATION qui appartient à l'AO : la phrase le dit, sans renvoi vers
 *  la fiche (son onglet ne voit pas les dossiers du coffre). */
export function phraseDUnDossierQuiEntre(t: TFunction, erreur: unknown): string | null {
    if (!erreur || typeof erreur !== 'object') return null
    const { code, details } = erreur as { code?: unknown; details?: Record<string, unknown> }
    if (code !== GED_FOLDER_OWNED_BY_TENDER || details?.entree !== true) return null
    const reference = details.tender_reference
    const phrase: unknown =
        typeof reference === 'string' && reference.trim()
            ? t('ged.folder_enter_owned_by_tender', { defaultValue: ENTREE_FR, reference })
            : t('ged.folder_enter_owned_by_tender_no_reference', { defaultValue: ENTREE_SANS_REF_FR })
    return typeof phrase === 'string' && phrase.trim() ? phrase : null
}

const ENTREE_FR =
    "Ce dossier de destination appartient à l'appel d'offres {{reference}} : on ne peut pas y ranger un dossier du coffre."
const ENTREE_SANS_REF_FR =
    "Ce dossier de destination appartient à un appel d'offres : on ne peut pas y ranger un dossier du coffre."

/** 422 — la destination est le dossier lui-même ou l'un de ses sous-dossiers
 *  (`api/_ged_folders_destination`, 2026-09-28). L'écran écarte ces cibles, mais sur son
 *  cache : le refus n'arrive que d'un arbre périmé, d'où « rafraîchissez ». */
export const FOLDER_CYCLE_DETECTED = 'FOLDER_CYCLE_DETECTED'

/** La phrase d'un déplacement qui refermerait l'arbre sur lui-même, ou `null`. */
export function phraseDUnDossierRangeSousLuiMeme(t: TFunction, erreur: unknown): string | null {
    if ((erreur as { code?: unknown } | null)?.code !== FOLDER_CYCLE_DETECTED) return null
    const phrase: unknown = t('ged.refus.folder_cycle', { defaultValue: CYCLE_FR })
    return typeof phrase === 'string' && phrase.trim() ? phrase : CYCLE_FR
}

const CYCLE_FR =
    "Un dossier ne se range ni dans lui-même, ni dans l'un de ses sous-dossiers : rafraîchissez l'arborescence, puis choisissez une autre destination."

/** 409 — un dossier voisin porte déjà ce nom (`api/_ged_folders_nom_pris`, EC-22 du
 *  2026-09-29) : créer, renommer ou ranger. Le code est celui des quatre onglets de fiche ;
 *  le message du serveur est en français, d'où la phrase de l'écran. Il sortait en 500. */
export const FOLDER_NAME_ALREADY_EXISTS = 'FOLDER_NAME_ALREADY_EXISTS'

/** La phrase d'un nom de dossier déjà pris à cet endroit, ou `null`. */
export function phraseDUnNomDeDossierPris(t: TFunction, erreur: unknown): string | null {
    if ((erreur as { code?: unknown } | null)?.code !== FOLDER_NAME_ALREADY_EXISTS) return null
    const phrase: unknown = t('ged.refus.folder_name_taken', { defaultValue: NOM_PRIS_FR })
    return typeof phrase === 'string' && phrase.trim() ? phrase : NOM_PRIS_FR
}

const NOM_PRIS_FR =
    'Un dossier porte déjà ce nom à cet endroit : choisissez un autre nom ou un autre emplacement.'

/**
 * L'erreur d'une porte du coffre qui a refusé la suppression d'un DOSSIER.
 *
 * 🔴 Mesuré le 2026-09-18 : `deleteFolder` levait `new Error('Failed to delete
 * folder: 409')` — un statut HTTP là où le serveur envoie la consultation, le
 * nombre de pièces et le `request_id`. Motif complet en tête de `ged-folders.ts`.
 *
 * Elle ne prend pas de geste : cette porte-là ne refuse que la suppression, et
 * la phrase d'un dossier est sa propre phrase — lue comme une pièce, elle dirait
 * « Ce document fait partie… » à quelqu'un qui supprime un dossier.
 *
 * 🔴 **F3, 2026-09-19 (soir) — la seconde lecture est PARTIE avec son refus.**
 * La décision PO Q1 a retiré côté serveur le 409 d'un dossier POSSÉDÉ mais vide,
 * et ses deux clés des DEUX catalogues : garder la branche, c'était appeler des
 * clés que plus rien ne traduit. La lecture de la CASCADE reste, et c'est la
 * seule : le dossier qui contient des pièces d'AO est toujours refusé, avec
 * `files_count`, et la phrase le dit. Pas de 410 ici : supprimer est un RETRAIT,
 * permis sur une consultation anonymisée.
 *
 * Le dossier qui porte une pièce ÉPINGLÉE (facture d'intervenant, pochette…) est refusé
 * lui aussi, par son propre code : `ged-dossier-epingle`.
 */
export async function erreurDunDossierDuCoffre(res: Response, repli: string): Promise<ApiError> {
    return erreurLue(
        res,
        repli,
        (e) =>
            phraseDuRefusDUnDossier(i18next.t, e) ??
            phraseDUnDossierQuiPorteUnePieceEpinglee(i18next.t, e) ??
            phraseDunDossierSysteme(i18next.t, e),
    )
}

/**
 * L'erreur du PATCH d'un DOSSIER : le renommer ou le déplacer.
 *
 * 🔴 B3, mesuré le 2026-09-19 : `updateFolder` levait `new Error('Failed to
 * update folder: ' + statut)` alors que la clé `folderMovedOwnedByTender` était
 * posée dans les deux catalogues sans aucun consommateur. ✅ F7 : le 422 du
 * dossier SYSTÈME y est chaîné — son message serveur est une phrase française en
 * dur. ✅ 2026-09-23 : le 410 aussi (renommer ou déplacer un dossier d'une
 * consultation anonymisée, ou en déplacer un DEDANS).
 *
 * 🔴 Le 409 d'un dossier LIBRE qu'on fait ENTRER dans l'arbre d'une consultation
 * (`_ged_folders_ao.refuser_dentrer_dans_une_consultation`) porte le MÊME code que
 * le dossier d'AO qu'on sort (C11). Lu par la phrase du déplacement, il disait « ce
 * dossier fait partie de l'appel d'offres » d'un dossier qui n'en fait PAS partie,
 * et renvoyait vers une fiche qui ne voit pas les dossiers du coffre. Le serveur le
 * marque `entree: true` ; il se lit donc EN PREMIER, avec sa propre phrase.
 */
export async function erreurDunDossierDeplace(res: Response, repli: string): Promise<ApiError> {
    return erreurLue(
        res,
        repli,
        (e) =>
            phraseDUnDossierQuiEntre(i18next.t, e) ??
            phraseDuRefusDUnDossierDeplace(i18next.t, e) ??
            phraseDUnDossierRangeSousLuiMeme(i18next.t, e) ??
            phraseDUnNomDeDossierPris(i18next.t, e) ??
            phraseDunDossierSysteme(i18next.t, e) ??
            phraseDUneConsultationAnonymisee(i18next.t, e),
    )
}

/**
 * L'erreur de la CRÉATION d'un dossier du coffre (F8, 2026-09-19 soir).
 *
 * `createFolder` jetait `new Error(message serveur || 'Failed to create folder:
 * 404')` : les deux refus légitimes de cette porte — parent introuvable, plafond
 * de PROFONDEUR du plan — sortaient donc en ANGLAIS chez un francophone, et le
 * `request_id` d'une vraie panne partait avec. Le 422 du dossier SYSTÈME est
 * chaîné derrière (« Documents rattachés »), et depuis le 2026-09-23 le 410 d'un
 * parent de consultation anonymisée.
 */
export async function erreurDeCreationDunDossier(res: Response, repli: string): Promise<ApiError> {
    return erreurLue(
        res,
        repli,
        (e) =>
            phraseDeCreationDunDossier(i18next.t, e) ??
            phraseDUnNomDeDossierPris(i18next.t, e) ??
            phraseDunDossierSysteme(i18next.t, e) ??
            phraseDUneConsultationAnonymisee(i18next.t, e),
    )
}

/**
 * Le moule des lecteurs d'erreur : lire le corps une fois, composer la phrase de
 * l'écran si le code est reconnu, garder tout le reste.
 *
 * ⚠️ L'ordre des replis est le contrat, jamais l'inverse :
 * 1. la phrase de l'écran (`lire`), qui connaît le geste — `PARENT_NOT_FOUND` y garde
 *    « le dossier parent n'existe plus… » ;
 * 2. la table TRANSVERSE (droit manquant, élément introuvable, saisie refusée…), HORS
 *    panne : sans elle, « Permission 'ged:rename' required for this action » ou « Value
 *    error, Folder name contains forbidden character: : » sortaient tels quels, en
 *    anglais (V13_V11_ISSUES/dette/0, 2026-09-28). Une panne (5xx) garde le message du
 *    serveur : `toastApiError` la dit par son statut, avec la référence et « Copier » ;
 * 3. le message du serveur, puis le repli technique.
 * Un toast vide ferait passer un refus pour un succès, et un statut HTTP ne dit rien.
 * Une phrase composée aux rangs 1 ou 2 est MARQUÉE (`phraseDEcran`) : `toastApiError` la dit
 * telle quelle, au lieu de la relire par le code.
 */
export async function erreurLue(
    res: Response,
    repli: string,
    lire: (erreur: { code: string; details: Record<string, unknown> }) => string | null,
): Promise<ApiError> {
    const corps = await res.json().catch(() => ({}))
    const { message, code, requestId, details } = extractStructuredApiError(corps)
    const lu = { code, details, statusCode: res.status }
    const phrase =
        lire({ code, details }) ?? (estUnIncident(lu) ? null : phraseDeRefusTransverse(lu, i18next.t))
    const erreur = new ApiError(phrase || message || `${repli}: ${res.status}`, res.status, code, requestId, details)
    if (phrase) erreur.phraseDEcran = true
    return erreur
}
